import {
	applyCachedRuntimeState,
	getLastConfigServer,
	joinUrl,
	loadCachedRuntimeState,
	normalizeBaseUrl,
	saveRuntimeState,
	sortServerList
} from './runtimeConfig'

const ENCRYPTION_KEY = 'box-im-secret-key-2024'

function getConfigValue(name, fallback = '') {
	return process.env[name] || fallback
}

function parseConfigServerUrls() {
	const urls = String(getConfigValue('VUE_APP_CONFIG_SERVER_URLS', '') || '')
		.split(',')
		.map(normalizeBaseUrl)
		.filter(Boolean)
	const cachedConfigServer = getLastConfigServer()
	return Array.from(new Set([cachedConfigServer, ...urls].filter(Boolean)))
}

function hexToBytes(hex = '') {
	const normalizedHex = String(hex || '').trim()
	const bytes = new Uint8Array(normalizedHex.length / 2)
	for (let index = 0; index < normalizedHex.length; index += 2) {
		bytes[index / 2] = parseInt(normalizedHex.slice(index, index + 2), 16)
	}
	return bytes
}

function getAesKeyBytes() {
	const key = ENCRYPTION_KEY.padEnd(32, '0').slice(0, 32)
	return new TextEncoder().encode(key)
}

async function decryptConfigPayload(cipherText = '') {
	const parts = String(cipherText || '').split(':')
	if (parts.length !== 2) {
		throw new Error('加密配置格式不正确')
	}
	if (!window.crypto?.subtle) {
		throw new Error('当前环境不支持 WebCrypto，无法解密服务器配置')
	}
	const key = await window.crypto.subtle.importKey('raw', getAesKeyBytes(), { name: 'AES-CBC' }, false, ['decrypt'])
	const plainBuffer = await window.crypto.subtle.decrypt({ name: 'AES-CBC', iv: hexToBytes(parts[0]) }, key, hexToBytes(parts[1]))
	const plainText = new TextDecoder().decode(plainBuffer)
	if (!plainText) {
		throw new Error('加密配置解密失败')
	}
	return JSON.parse(plainText)
}

async function requestRaw(url, timeout = 3000) {
	const controller = new AbortController()
	const timer = setTimeout(() => controller.abort(), timeout)
	try {
		const response = await fetch(url, {
			method: 'GET',
			signal: controller.signal
		})
		const data = await response.json().catch(() => null)
		return {
			statusCode: response.status,
			data
		}
	} finally {
		clearTimeout(timer)
	}
}

function normalizeConfigPayload(payload) {
	if (Array.isArray(payload)) {
		return {
			version: 1,
			expireAt: Date.now() + Number(getConfigValue('VUE_APP_RUNTIME_SERVER_CACHE_TTL', 0)),
			servers: payload
		}
	}
	return {
		version: payload?.version || 1,
		expireAt: Number(payload?.expireAt || 0),
		servers: Array.isArray(payload?.servers) ? payload.servers : []
	}
}

async function fetchEncryptedConfig(configServerUrl) {
	const path = getConfigValue('VUE_APP_CONFIG_FETCH_PATH', '/server-config/getUrlConfig')
	const timeout = Number(getConfigValue('VUE_APP_CONFIG_FETCH_TIMEOUT', 3000))
	const response = await requestRaw(joinUrl(configServerUrl, path), timeout)
	if (response.statusCode !== 200 || !response.data) {
		throw new Error(`配置中心响应异常: ${response.statusCode}`)
	}
	if (!response.data.encrypted || !response.data.data) {
		throw new Error('配置中心返回格式不正确')
	}
	return decryptConfigPayload(response.data.data)
}

async function probeBusinessServer(server) {
	const path = getConfigValue('VUE_APP_SERVER_PROBE_PATH', '/system/config')
	const timeout = Number(getConfigValue('VUE_APP_SERVER_PROBE_TIMEOUT', 3000))
	const response = await requestRaw(joinUrl(server.httpUrl, path), timeout)
	if (response.statusCode !== 200) {
		throw new Error(`业务服务探测失败: ${response.statusCode}`)
	}
	return response
}

async function pickAvailableBusinessServer(servers = []) {
	const sortedServers = sortServerList(servers)
	let lastError = null
	for (const server of sortedServers) {
		try {
			await probeBusinessServer(server)
			return {
				server,
				servers: sortedServers
			}
		} catch (error) {
			lastError = error
			console.warn('[bootstrap-server] 业务服务不可用:', server.httpUrl, error)
		}
	}
	throw lastError || new Error('没有可用的业务服务')
}

async function tryBootstrapFromConfigServer(configServerUrl) {
	const payload = normalizeConfigPayload(await fetchEncryptedConfig(configServerUrl))
	if (!payload.servers.length) {
		throw new Error('配置中心未返回业务服务列表')
	}
	const { server, servers } = await pickAvailableBusinessServer(payload.servers)
	return saveRuntimeState({
		configServerUrl,
		baseUrl: server.httpUrl,
		servers,
		payload,
		expireAt: payload.expireAt
	})
}

async function bootstrapServerConfig() {
	const configServerList = parseConfigServerUrls()
	let lastError = null

	for (const configServerUrl of configServerList) {
		try {
			const result = await tryBootstrapFromConfigServer(configServerUrl)
			console.log('[bootstrap-server] 动态配置初始化成功:', result.baseUrl)
			return result
		} catch (error) {
			lastError = error
			console.warn('[bootstrap-server] 配置中心不可用:', configServerUrl, error)
		}
	}

	const cachedRuntimeState = loadCachedRuntimeState()
	if (cachedRuntimeState) {
		applyCachedRuntimeState(cachedRuntimeState)
		console.warn('[bootstrap-server] 使用本地缓存的业务地址:', cachedRuntimeState.baseUrl)
		return cachedRuntimeState
	}

	throw lastError || new Error('动态配置初始化失败')
}

export {
	bootstrapServerConfig
}
